import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VerifiedIcon from '@mui/icons-material/Verified';
import api from '../utils/api';

const CertificateVerify = () => {
  const { verificationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (verificationId) {
      verifyCertificate();
    }
  }, [verificationId]);

  const verifyCertificate = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/certificate/verify/${verificationId}`);
      const data = await res.json();
      setResult(data);
    } catch (err) {
      console.error('Error verifying certificate:', err);
      setError('Failed to verify certificate');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        {/* Header with Logo */}
        <Box sx={{ mb: 3 }}>
          <img 
            src="/assets/Vidyaranyam_Logo.jpeg" 
            alt="Vidyaranyam Logo" 
            style={{ width: 80, height: 80, objectFit: 'contain', marginBottom: 16 }}
          />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#7f4b2d' }}>
            पट्टा प्रमाणपत्र सत्यापनम्
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Certificate Verification
          </Typography>
        </Box>

        {result?.valid ? (
          <Box>
            {/* Valid Certificate Badge */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 1, 
              mb: 3,
              p: 2,
              backgroundColor: '#e8f5e9',
              borderRadius: 2
            }}>
              <VerifiedIcon sx={{ fontSize: 40, color: '#2e7d32' }} />
              <Box>
                <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                  प्रमाणिकम् प्रमाणपत्रम्
                </Typography>
                <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                  Verified Certificate
                </Typography>
              </Box>
            </Box>

            <Chip
              icon={<CheckCircleIcon />}
              label={result.data.status === 'issued' ? 'निर्गतम् (Issued)' : result.data.status}
              color="success"
              sx={{ mb: 3 }}
            />

            {/* Certificate Details - Key fields as requested */}
            <Box sx={{ 
              textAlign: 'left', 
              mt: 3, 
              p: 3, 
              backgroundColor: '#fafafa', 
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}>
              {/* Vatu Name */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  विद्यार्थी नाम (Student Name)
                </Typography>
                <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 600 }}>
                  {result.data.vatu_name} शर्म्मा
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Guruvu Name */}
              {result.data.guruvu_name && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    गुरुः नाम (Teacher Name)
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {result.data.guruvu_name}
                  </Typography>
                </Box>
              )}
              
              {/* Pathasala Name */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  पाठशाला (Institution)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {result.data.pathasala_name}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Patta Type */}
              {result.data.patta_type && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    पट्टा प्रकारः (Patta Type)
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 600, 
                    color: '#b71c1c',
                    fontSize: '1.1rem'
                  }}>
                    {result.data.patta_type}
                  </Typography>
                </Box>
              )}
              
              {/* Shreni (Grade) */}
              {result.data.shreni && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    श्रेणी (Grade)
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#1565c0' }}>
                    {result.data.shreni}
                  </Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              {/* Year and Date */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {result.data.telugu_year && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      वत्सरः (Year)
                    </Typography>
                    <Typography variant="body2">
                      {result.data.telugu_year}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    दिनाङ्कः (Issue Date)
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(result.data.issue_date)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box>
            <CancelIcon sx={{ fontSize: 80, color: '#c62828', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#c62828', fontWeight: 600, mb: 2 }}>
              प्रमाणपत्रं अप्रामाणिकम्
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Invalid Certificate
            </Typography>
            <Typography color="text.secondary">
              {result?.message || 'एतत् प्रमाणपत्रं अस्माकं अभिलेखेषु न प्राप्तम्।'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This certificate could not be found in our records.
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            श्रीविद्यारण्यवैदिकविद्याविहारं ट्रस्ट्
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Sri Vidyaranya Vaidika Vidya Viharam Trust
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            सत्यापन सङ्केतः: {verificationId}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default CertificateVerify;
